<template>
  <div>
    <section>
      <b-col class="d-flex justify-content-between px-0 pt-25">
        <b-button
          v-b-modal.modal-add-edit-ad-formats
          variant="primary"
          :disabled="isLoading"
          size="sm"
        >
          <GjIcon
            class="mr-50"
            name="Plus"
          />
          {{ $tk('ClientPortal.Account.Edit.Add') }}
        </b-button>
        <SearchInputField
          :placeholder="$tk('ClientPortal.Admin.Language.SearchPlaceholder')"
          :disabled="isLoading"
          @onInputSearch="onSearchChange"
        />
      </b-col>
      <b-card
        class="my-50 admin-table--no-pagination-smaller"
        no-body
      >
        <Table
          class="ad-formats-table admin-table__data"
          :data="adFormats"
          :columns="adFormatsColumns"
          :is-loading="isLoading"
          type-of-table="adFormats"
          :enable-pagination="false"
          :enable-sort="false"
        />
        <b-modal
          id="modal-add-edit-ad-formats"
          :ok-title="$tk('ClientPortal.Form.Actions.Save')"
          cancel-variant="outline-secondary"
          :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
          centered
          size="md"
          :ok-disabled="isFormComplete()"
          :title="$tk('ClientPortal.Filters.AdFormat')"
          no-enforce-focus
          @show="getAdFromatModel"
          @change="getAdFormats"
          @ok="save"
        >
          <b-form-group
            :label="$tk('ClientPortal.Admin.AdFormats.Name')"
            label-size="xs"
            :invalid-feedback="errors.name"
          >
            <b-form-input
              v-model="adFormatsModel.name"
              size="sm"
              type="text"
              :state="errors.name == '' ? null : false"
            />
          </b-form-group>
          <b-form-group
            :label="$tk('ClientPortal.Admin.AdFormats.ChooseDevice')"
            label-size="xs"
          >
            <v-select
              v-model="adFormatsModel.deviceType"
              :options="adFormatsModel.deviceTypes"
              label="name"
              taggable
              select-on-tab
              :clearable="false"
              :reduce="deviceType => deviceType.id"
            />
          </b-form-group>
          <div v-if="adFormatsModel.deviceType !== DEVICE_TYPE.MOBILE">
            <b-form-group
              :label="$tk('ClientPortal.Admin.AdFormats.Width')"
              label-size="xs"
              :invalid-feedback="errors.width"
            >
              <b-form-input
                v-model.number="adFormatsModel.width"
                size="sm"
                type="number"
                :state="errors.width == '' ? null : false"
              />
            </b-form-group>
            <b-form-group
              :label="$tk('ClientPortal.Admin.AdFormats.Height')"
              label-size="xs"
              :invalid-feedback="errors.height"
            >
              <b-form-input
                v-model.number="adFormatsModel.height"
                size="sm"
                type="number"
                :state="errors.height == '' ? null : false"
              />
            </b-form-group>
          </div>
          <div v-if="adFormatsModel.deviceType !== DEVICE_TYPE.DESKTOP">
            <b-form-group
              :label="$tk('ClientPortal.Admin.AdFormats.MobileWidth')"
              label-size="xs"
            >
              <b-form-input
                v-model="adFormatsModel.mobileWidth"
                size="sm"
                type="number"
              />
            </b-form-group>
            <b-form-group
              :label="$tk('ClientPortal.Admin.AdFormats.MobileHeight')"
              label-size="xs"
              class="mb-0"
            >
              <b-form-input
                v-model="adFormatsModel.mobileHeight"
                size="sm"
                type="number"
              />
            </b-form-group>
          </div>
        </b-modal>
      </b-card>
    </section>
  </div>
</template>

<script>
import { VBModal } from 'bootstrap-vue'
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import GjIcon from '@gjirafatech/gjirafa-icons/Icon.vue'
import Table from '@/components/Table.vue'
import SearchInputField from '@/components/SearchInputField.vue'
import notification from '@/mixins/toastification'
import AdFormatsService from '@/api/admin/adFormats.service'
import translation from '@/mixins/translate'
import store from '@/store'
import vSelect from 'vue-select'

const DEVICE_TYPE = {
  ALL: 1,
  DESKTOP: 2,
  MOBILE: 3,
}

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    // Bootstrap Components
    SearchInputField,
    GjIcon,
    Table,
    vSelect,
  },
  mixins: [notification],
  setup() {
    const adFormatsColumns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.Name'),
        field: 'name',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.Width'),
        field: 'width',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.Height'),
        field: 'height',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.MobileWidth'),
        field: 'mobileWidth',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.MobileHeight'),
        field: 'mobileHeight',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.LastModified'),
        field: 'lastModified',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.AdFormats.ModifiedBy'),
        field: 'modifiedBy',
      },
    ]
    const adFormats = computed(() => store.getters.allAdFormats)
    const isLoading = ref(false)
    const adFormatsModel = ref({})
    const errors = reactive({
      name: '',
      width: '',
      height: '',
    })

    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getAdFormats')
      adFormats.value.searchTerm = ''
      isLoading.value = false
    })

    const getAdFromatModel = async () => {
      const adFormatId = store.getters.getAdFormatId
      adFormatsModel.value = await AdFormatsService.getAdFormat(adFormatId)
      errors.name = ''
      errors.width = ''
      errors.height = ''
      store.commit('SET_AD_FORMAT_MODEL', { id: 0 })
    }

    const getAdFormats = async () => { await store.dispatch('getAdFormats') }

    const isFormComplete = () => {
      const {
        name, width, height, mobileWidth, mobileHeight,
      } = adFormatsModel.value
      return !(name
       && (adFormatsModel.value.deviceType !== DEVICE_TYPE.MOBILE ? width && height : true)
       && (adFormatsModel.value.deviceType !== DEVICE_TYPE.DESKTOP ? mobileWidth && mobileHeight : true)
      )
    }
    const rowAnimation = () => {
      const newRow = Array.from(document.querySelectorAll('.ad-formats-table > .vgt-wrap > .vgt-inner-wrap > .vgt-responsive > table > tbody > tr:first-child')).pop()
      newRow.classList.remove('table-row-animation')
      // eslint-disable-next-line no-unused-expressions
      newRow.offsetHeight // trigger reflow
      newRow.classList.add('table-row-animation')
    }
    const save = async () => {
      if (adFormatsModel.value.deviceType === DEVICE_TYPE.DESKTOP) {
        adFormatsModel.value.mobileHeight = null
        adFormatsModel.value.mobileWidth = null
      } else if (adFormatsModel.value.deviceType === DEVICE_TYPE.MOBILE) {
        adFormatsModel.value.height = null
        adFormatsModel.value.width = null
      }
      try {
        const response = await AdFormatsService.updateAdFormat(adFormatsModel.value)
        if (response) {
          notification.methods.success(translation.methods.$tk('ClientPortal.Admin.ChangesHaveBeenSavedSuccesfully'))
        } else {
          notification.methods.error(translation.methods.$tk('ClientPortal.Admin.ErrorSavingChanges'))
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getAdFormats')
        if (adFormatsModel.value.id === 0) {
          rowAnimation()
        }
      }
    }
    const onSearchChange = async value => {
      adFormats.value.searchTerm = value
    }
    return {
      isLoading,
      adFormats,
      adFormatsModel,
      errors,
      getAdFromatModel,
      save,
      getAdFormats,
      isFormComplete,
      adFormatsColumns,
      onSearchChange,
      DEVICE_TYPE,
    }
  },
}
</script>
<style lang="scss">
@import '@/assets/scss/views/added_row_animation.scss';
</style>
